/**
 * CannedResponsePicker Component
 *
 * Searchable dropdown for selecting canned response templates.
 * Allows support staff to quickly insert pre-written responses.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 2: Ticket Detail Page Components
 * Location: apps/web/src/components/support/CannedResponsePicker.jsx
 */

import React, { useState } from 'react';
import { Search, FileText, Check } from 'lucide-react';
import { useCannedResponses, useSearchCannedResponses } from '../../hooks/support/useSupportTicket';

export function CannedResponsePicker({ requestType, onSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch responses for the ticket's category
  const { responses: categoryResponses, loading: loadingCategory } = useCannedResponses(requestType);

  // Search functionality
  const { results: searchResults, searching, search } = useSearchCannedResponses();

  const handleSearchChange = (  _e) => {
    const query = _e.target.value;
    setSearchQuery(query);

    if (query.length >= 2) {
      search(query);
    }
  };

  const handleSelect = (response) => {
    setSelectedId(response.id);
    onSelect(response.content);
    setSearchQuery('');
    setIsExpanded(false);
  };

  // Determine which responses to display
  const displayedResponses = searchQuery.length >= 2 ? searchResults : categoryResponses;
  const isLoading = searchQuery.length >= 2 ? searching : loadingCategory;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-900">
          Quick Responses
        </label>
        {categoryResponses.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {isExpanded ? 'Show Less' : `Show All (${categoryResponses.length})`}
          </button>
        )}
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search canned responses..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-xs text-gray-600 mt-2">Loading responses...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && displayedResponses.length === 0 && (
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            {searchQuery.length >= 2
              ? 'No responses found for your search'
              : 'No canned responses available for this category'}
          </p>
        </div>
      )}

      {/* Response List */}
      {!isLoading && displayedResponses.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {displayedResponses
            .slice(0, isExpanded ? undefined : 5)
            .map((response) => (
              <button
                key={response.id}
                onClick={() => handleSelect(response)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedId === response.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {response.title}
                      </h4>
                      {selectedId === response.id && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {response.content}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                        {response.category.replace('_', ' ')}
                      </span>
                      {response.usage_count > 0 && (
                        <span className="text-xs text-gray-500">
                          Used {response.usage_count} {response.usage_count === 1 ? 'time' : 'times'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
        </div>
      )}

      {/* Show hint for more results */}
      {!isLoading &&
        !isExpanded &&
        displayedResponses.length > 5 &&
        searchQuery.length < 2 && (
          <p className="text-xs text-center text-gray-500 py-2">
            {displayedResponses.length - 5} more responses available
          </p>
        )}
    </div>
  );
}

export default CannedResponsePicker;
